import './SaveFilters.scss';
import { useTranslator } from '@Localization';
import { Button } from '@mui/joy';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { ButtonWithLoader } from '../../../../../../../design/buttons/ButtonWithLoader';
import { Select } from '../../../../../../../design/inputs/select/Select';
import { S_TextInput } from '../../../../../../../design/inputs/textInput/S_TextInput';
import { S_FieldLabel } from '../../../../../../../design/labels/fieldLabel/S_FIeldLabel';
import { InputStatus } from '../../../../../../models/error.model';
import { ISavedFilter } from '../../imageFilters.model';
import { FilterContent } from './FilterContent';

interface ISaveFilterProps {
  savedFilters: ISavedFilter[];
  selectedFilter: ISavedFilter | undefined;
  isLoading: boolean;
  newFilterName: string;
  newFilterNameStatus: InputStatus;
  isNewFilterNameUsed: boolean;
  showDeleteConfirmation: boolean;
  showFilterLoadedMessage: boolean;
  showFilterSavedMessage: boolean;
  onNewFilterNameChange: (newName: string) => void;
  onNewFilterNameBlur: () => void;
  onFilterSave: () => Promise<void>;
  onFilterOverrideConfirmed: () => Promise<void>;
  onFilterOverrideDeclined: () => void;
  onFilterSelected: (selectedFilter: ISavedFilter | undefined) => void;
  onSelectedFilterDelete: () => void;
  onSelectedFilterLoad: () => void;
  onFilterDeleteConfirmed: () => Promise<void>;
  onFilterDeleteDeclined: () => void;
}

export const SaveFilters = observer((props: ISaveFilterProps): ReactElement => {
  const { t } = useTranslator();

  const showSuccessMessages = !props.showDeleteConfirmation && !props.isNewFilterNameUsed;
  const selectedOption = props.savedFilters.find((x) => x.id === props.selectedFilter?.id) ?? null;

  const options = props.savedFilters.map((x) => ({
    value: x,
    label: x.name,
  }));

  return (
    <FilterContent className='filter-save'>
      <div>
        <S_FieldLabel>{t('select_filter_to_load')}</S_FieldLabel>
        <div className='input-wrapper'>
          <Select
            placeholder={t('select_dots')}
            value={options.find((x) => x.value === selectedOption)}
            onChange={(_, v) => {
              props.onFilterSelected(v.value);
            }}
            options={options}
          />
          <Button
            variant='outlined'
            onClick={props.onSelectedFilterDelete}
            disabled={props.isLoading || !props.selectedFilter || props.isNewFilterNameUsed}
          >
            {t('delete')}
          </Button>
          <Button
            onClick={props.onSelectedFilterLoad}
            disabled={props.isLoading || !props.selectedFilter || props.isNewFilterNameUsed}
          >
            {t('load')}
          </Button>
        </div>
        <div className='message-wrapper'>
          {showSuccessMessages && (
            <div className={classNames({ 'success-text': true, 'fade-out': !props.showFilterLoadedMessage, visible: props.showFilterLoadedMessage })}>
              {t('filter_loaded_successfully')}
            </div>
          )}
        </div>
      </div>
      <div>
        <S_FieldLabel>{t('save_filter')}</S_FieldLabel>
        <div className='input-wrapper'>
          <S_TextInput
            value={props.newFilterName}
            error={props.newFilterNameStatus.isValid === false}
            type='text'
            placeholder={t('type_filter_name')}
            onChange={props.onNewFilterNameChange}
            onBlur={props.onNewFilterNameBlur}
            maxLength={200}
          />
          <ButtonWithLoader
            color='primary'
            onClick={props.onFilterSave}
            disabled={props.isLoading || !props.newFilterName}
            loaderKey='filter-save'
          >
            {t('save')}
          </ButtonWithLoader>
        </div>
      </div>
      {props.isNewFilterNameUsed && (
        <div className='filter-save-warning'>
          <div className='warning-text'>{t('filter_override_question', { filterName: props.newFilterName })}</div>
          <div className='filter-save-warning-controls'>
            <Button
              variant='outlined'
              onClick={props.onFilterOverrideDeclined}
              disabled={props.isLoading}
            >
              {t('no')}
            </Button>
            <ButtonWithLoader
              onClick={props.onFilterOverrideConfirmed}
              disabled={props.isLoading}
              loaderKey='filter-save'
            >
              {t('yes')}
            </ButtonWithLoader>
          </div>
        </div>
      )}
      {props.showDeleteConfirmation && (
        <div className='filter-save-warning'>
          <div className='warning-text'>{t('filter_delete_question')}</div>
          <div className='filter-save-warning-controls'>
            <Button
              variant='outlined'
              onClick={props.onFilterDeleteDeclined}
              disabled={props.isLoading}
            >
              {t('no')}
            </Button>
            <ButtonWithLoader
              onClick={props.onFilterDeleteConfirmed}
              disabled={props.isLoading}
              loaderKey='filter-delete'
            >
              {t('yes')}
            </ButtonWithLoader>
          </div>
        </div>
      )}
      <div className='message-wrapper'>
        {showSuccessMessages && (
          <div className={classNames({ 'success-text': true, 'fade-out': !props.showFilterSavedMessage, visible: props.showFilterSavedMessage })}>
            {t('filter_saved_successfully')}
          </div>
        )}
      </div>
    </FilterContent>
  );
});
